import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Location, NgIf } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { SettingsCategory } from '../../../models/settings.model';
import { SettingsService } from '../../../services/settings.service';
import { environment } from '../../../../environments/environment';
import { BehaviorSubject, catchError, forkJoin, Observable, of, tap } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { StateSnackBarService } from '../../../services/state-snack-bar.service';
import { ImageUploadService } from '../../../services/image-upload.service';

@Component({
  selector: 'app-settings-detail',
  templateUrl: './settings-detail.component.html',
  styleUrls: ['./settings-detail.component.scss'],
  standalone: true,
  imports: [
    MatIcon,
    MatIconButton,
    NgIf,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    RouterLink,
    MatButton,
    MatError,
  ],
})
export class SettingsDetailComponent implements OnInit {
  public category?: SettingsCategory;
  public settingsForm!: FormGroup;
  public dataSource: any;
  private initialFormValue: any;
  private authService = inject(AuthService);
  private snackbarService = inject(StateSnackBarService);
  private imageUploadService = inject(ImageUploadService);

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('profilBild') profilePic!: ElementRef<HTMLImageElement>;
  @ViewChild('username') username!: ElementRef<HTMLInputElement>;
  @ViewChild('email') email!: ElementRef<HTMLInputElement>;

  showUsernameError$ = new BehaviorSubject(false);
  showEmailError$ = new BehaviorSubject(false);

  constructor(
    private route: ActivatedRoute,
    private settingsService: SettingsService,
    private location: Location,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.category = this.settingsService.getCategoryById(id);
      if (this.category) {
        // Datenquelle holen und in der neuen Eigenschaft speichern
        this.dataSource = this.settingsService.getDataFor(this.category.dataSourceKey);
        // Die Daten an buildForm übergeben
        this.buildForm(this.category, this.dataSource);
      }
    }

    if (this.settingsForm.contains('username')) {
      this.settingsForm?.get('username')?.valueChanges.subscribe((value) => {
        this.settingsService.checkUsernameAvailability(value).subscribe((val) => {
          this.showUsernameError$.next(val);
          if (val) {
            this.settingsForm.setErrors({ usernameTaken: true });
          } else {
            delete this.settingsForm.errors?.['usernameTaken'];
          }
        });
      });
    }

    if (this.settingsForm.contains('email')) {
      this.settingsForm?.get('email')?.valueChanges.subscribe((value) => {
        this.settingsService.checkEmailAvailability(value).subscribe((val) => {
          this.showEmailError$.next(val);
          if (val) {
            this.settingsForm.setErrors({ emailTaken: true });
          } else {
            delete this.settingsForm.errors?.['emailTaken'];
          }
        });
      });
    }
  }

  private buildForm(category: SettingsCategory, data: any): void {
    const formControls: { [key: string]: any } = {};
    // const data = this.settingsService.getDataFor(category.dataSourceKey); // <-- Diese Zeile ist jetzt in ngOnInit

    category.items.forEach((item) => {
      if (['text', 'email', 'textarea', 'toggle', 'username', 'password'].includes(item.type)) {
        const value = (data as any)[item.key] || '';
        formControls[item.key] = [value, item.validators || []];
      }
    });

    this.settingsForm = this.fb.group(formControls);
    this.initialFormValue = this.settingsForm.value;
  }

  save(): void {
    if (this.settingsForm.invalid || !this.category) {
      return;
    }

    const updateCalls: Observable<any>[] = [];

    this.category.items.forEach((item) => {
      const control = this.settingsForm.get(item.key);

      if (control && control.dirty && control.valid) {
        switch (item.key) {
          case 'username':
            updateCalls.push(
              this.settingsService.updateUsername(control.value).pipe(
                tap((newUserInfo) => {
                  this.authService.updateUserProfile(newUserInfo);
                }),
              ),
            );
            break;
          case 'email':
            updateCalls.push(
              this.settingsService.updateEmail(control.value).pipe(
                tap((newUserInfo) => {
                  this.authService.updateUserProfile(newUserInfo);
                }),
              ),
            );
            break;
          case 'password':
            updateCalls.push(
              this.settingsService.updatePassword(control.value).pipe(
                tap((newUserInfo) => {
                  this.authService.updateUserProfile(newUserInfo);
                }),
              ),
            );
            break;
          case 'bio':
            break;
          // Füge hier weitere 'case' für andere Felder hinzu
        }
      }
    });

    if (updateCalls.length === 0) {
      return;
    }

    forkJoin(updateCalls)
      .pipe(
        catchError((err) => {
          this.snackbarService.error({ message: 'Fehler beim Speichern', subMessage: err.message });
          console.error('Fehler beim Speichern:', err);
          return of(null);
        }),
      )
      .subscribe((results) => {
        if (results) {
          this.snackbarService.success();
          this.initialFormValue = this.settingsForm.value;
          this.settingsForm.reset(this.initialFormValue);
        }
      });
  }

  discard(): void {
    this.settingsForm.reset(this.initialFormValue);
  }

  goBack(): void {
    this.location.back();
  }

  openFilePicker(): void {
    this.fileInput.nativeElement.value = '';
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    let fileList: FileList | null = element.files;
    if (fileList && fileList.length > 0) {
      const file = fileList[0];
      this.imageUploadService.uploadProfilePicture(file).subscribe((updatedProfile) => {
        this.authService.updateUserProfile(updatedProfile);
        this.snackbarService.success({ message: 'Profilbild erfolgreich aktualisiert!' });

        this.profilePic.nativeElement.src = environment.backend + updatedProfile.profilePictureUrl;
      });
    }
  }

  protected readonly environment = environment;
  protected readonly open = open;
}
